import React, { useState, useEffect } from 'react';
import { Question } from '../';
import axios from 'axios';
import { fetchAuthSession } from '@aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Quiz({ questions: propQuestions, onQuizEnd }) {
    const [questions, setQuestions] = useState([]);
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [answers, setAnswers] = useState({});
    const [startTime, setStartTime] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        if (propQuestions && propQuestions.length > 0) {
            const mappedQuestions = propQuestions.map(q => ({
                ...q,
                _id: q.questionId || q._id, // Map questionId to _id for compatibility
                text: q.questionText || q.text, // Normalize field
                subject: q.category || q.subject, // Map category to subject
            }));
            setQuestions(mappedQuestions);
            setStartTime(Date.now());
        }
    }, [propQuestions]);

    // Timer effect
    useEffect(() => {
        if (!startTime) return;
        const timer = setInterval(() => {
            setElapsedTime(Math.round((Date.now() - startTime) / 1000));
        }, 1000);
        return () => clearInterval(timer);
    }, [startTime]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const handleAnswer = (questionId, answer) => {
        setAnswers({ ...answers, [questionId]: answer });
    };

    const nextQuestion = () => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        }
    };

    const prevQuestion = () => {
        if (currentQuestion > 0) {
            setCurrentQuestion(currentQuestion - 1);
        }
    };

    const goToQuestion = (index) => {
        setCurrentQuestion(index);
    };

    const submitQuiz = async () => {
        const timeTaken = Math.round((Date.now() - startTime) / 1000);
        setIsSubmitting(true);

        try {
            // Get auth token
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            // Format answers as object { questionId: answer }
            const formattedAnswers = {};
            questions.forEach(q => {
                if (answers[q._id]) {
                    formattedAnswers[q._id] = answers[q._id];
                }
            });

            // Derive quiz category from the actual questions
            const uniqueSubjects = [...new Set(questions.map(q => q.subject).filter(Boolean))];
            const quizCategory = uniqueSubjects.length === 1 ? uniqueSubjects[0] : 'Mixed';

            // Submit to AWS backend for grading
            const response = await axios.post(`${API_BASE_URL}/api/quiz/submit`, {
                answers: formattedAnswers,
                timeSpentSeconds: timeTaken,
                quizCategory,
                subjects: uniqueSubjects,
            }, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            });

            // Backend returns graded results with correctAnswer for each question
            const { score, results } = response.data;

            // Build correctAnswers and explanations map from results
            const correctAnswers = {};
            const explanations = {};
            results.forEach(result => {
                correctAnswers[result.questionId] = result.correctAnswer;
                if (result.explanation) {
                    explanations[result.questionId] = result.explanation;
                }
            });

            // Merge correct answers and explanations back into questions for Results component
            const questionsWithAnswers = questions.map(q => ({
                ...q,
                correctAnswer: correctAnswers[q._id],
                explanation: explanations[q._id] || null
            }));

            onQuizEnd(score, timeTaken, questionsWithAnswers, answers);
        } catch (err) {
            console.error('Submit error:', err.response || err);
            alert('Failed to submit quiz. Please try again.');
            setIsSubmitting(false);
        }
    };

    if (!questions.length) {
        return (
            <div className="min-h-screen bg-white font-quicksand flex items-center justify-center">
                <div className="text-center">
                    <div className="text-6xl mb-4 animate-bounce">📝</div>
                    <p className="text-xl text-study-black font-semibold">Loading Quiz...</p>
                </div>
            </div>
        );
    }

    const progress = ((currentQuestion + 1) / questions.length) * 100;
    const answeredCount = Object.keys(answers).length;

    return (
        <div className="min-h-screen bg-gray-50 font-quicksand">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-4">
                            <span className="text-2xl">📝</span>
                            <div>
                                <h1 className="text-lg font-bold text-study-black">Quiz in Progress</h1>
                                <p className="text-sm text-gray-500">
                                    {answeredCount} of {questions.length} answered
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold text-study-black">{formatTime(elapsedTime)}</div>
                            <p className="text-xs text-gray-500">Time Elapsed</p>
                        </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-gradient-to-r from-study-teal to-study-pink h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Question Navigator (Desktop) */}
                    <div className="hidden lg:block">
                        <div className="bg-white rounded-2xl shadow-md p-4 border border-gray-100 sticky top-28">
                            <h3 className="text-sm font-bold text-study-black mb-3">Questions</h3>
                            <div className="grid grid-cols-5 gap-2">
                                {questions.map((q, index) => {
                                    const isAnswered = answers[q._id];
                                    const isCurrent = index === currentQuestion;
                                    return (
                                        <button
                                            key={q._id || index}
                                            onClick={() => goToQuestion(index)}
                                            className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${
                                                isCurrent
                                                    ? 'bg-study-teal text-study-black ring-2 ring-study-black'
                                                    : isAnswered
                                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                            }`}
                                        >
                                            {index + 1}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                                    <span className="w-3 h-3 bg-green-100 rounded"></span>
                                    <span>Answered</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="w-3 h-3 bg-gray-100 rounded"></span>
                                    <span>Not answered</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Question Card */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-2xl shadow-md p-6 md:p-8 border border-gray-100">
                            {/* Question Number */}
                            <div className="flex items-center justify-between mb-6">
                                <span className="text-sm font-bold text-gray-500">
                                    Question {currentQuestion + 1} of {questions.length}
                                </span>
                                {questions[currentQuestion].subject && (
                                    <span className="bg-study-teal/20 text-study-black text-sm font-semibold px-3 py-1 rounded-full">
                                        {questions[currentQuestion].subject}
                                    </span>
                                )}
                            </div>

                            {/* Question Content */}
                            <Question
                                question={questions[currentQuestion]}
                                onAnswer={handleAnswer}
                                selectedAnswer={answers[questions[currentQuestion]._id]}
                            />

                            {/* Navigation */}
                            <div className="mt-8 pt-6 border-t border-gray-200">
                                <div className="flex items-center justify-between">
                                    <button
                                        onClick={prevQuestion}
                                        disabled={currentQuestion === 0}
                                        className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-study-black bg-gray-100 hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span>←</span> Previous
                                    </button>

                                    {/* Mobile Question Dots */}
                                    <div className="flex lg:hidden gap-1 overflow-x-auto max-w-[120px]">
                                        {questions.slice(
                                            Math.max(0, currentQuestion - 2),
                                            Math.min(questions.length, currentQuestion + 3)
                                        ).map((q, idx) => {
                                            const actualIndex = Math.max(0, currentQuestion - 2) + idx;
                                            const isAnswered = answers[q._id];
                                            const isCurrent = actualIndex === currentQuestion;
                                            return (
                                                <button
                                                    key={q._id || actualIndex}
                                                    onClick={() => goToQuestion(actualIndex)}
                                                    className={`w-2 h-2 rounded-full transition-all ${
                                                        isCurrent
                                                            ? 'w-4 bg-study-teal'
                                                            : isAnswered
                                                            ? 'bg-green-400'
                                                            : 'bg-gray-300'
                                                    }`}
                                                />
                                            );
                                        })}
                                    </div>

                                    {currentQuestion < questions.length - 1 ? (
                                        <button
                                            onClick={nextQuestion}
                                            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-study-black bg-study-teal hover:bg-study-yellow transition-all"
                                        >
                                            Next <span>→</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={submitQuiz}
                                            disabled={isSubmitting}
                                            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-study-black bg-gradient-to-r from-study-pink to-study-teal hover:shadow-lg transition-all disabled:opacity-50"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="animate-spin">⏳</span> Submitting...
                                                </>
                                            ) : (
                                                <>Submit Quiz</>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Unanswered Warning */}
                        {answeredCount < questions.length && currentQuestion === questions.length - 1 && (
                            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                                <p className="text-yellow-800 text-sm">
                                    <strong>Note:</strong> You have {questions.length - answeredCount} unanswered question{questions.length - answeredCount !== 1 ? 's' : ''}.
                                    Unanswered questions will be marked as incorrect.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Quiz;